import { tw } from "@careinfo/tailwind";
import {
  ColumnDef,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDebounce } from "../../../customHooks/useDebounceHook";
import useGetHook from "../../../customHooks/useGetHook";
import TableBody from "./components/Body";
import TableHead from "./components/Head";
import TablePagination from "./components/Navigation";
import TableSearch from "./components/Search";
import Template, { TableBodyLoading } from "./loading";
//@ts-ignore
import { TableSettingsLayout } from "@careinfo/layout";

interface IDataTable {
  url: any;
  columns: ColumnDef<any, any>[];
  params?: Record<string, any>;
  queryKey: string;
  limit?: number;
  actionTools?: any;
  tableName?: string;
  customSearch?: boolean;
  addNew?: {
    path: string;
    label: string;
  };
  onRowClick?: (row: any) => void;
}

/**
 * Used to display the array of data
 * @param {IDataTable} props - props of data table
 *
 * @property `addNew`  can be added to show a button to redirect to
 * add entry `{ path:  string, label: string }`
 * @property `customSearch` can be add to place a search element in document and
 * the search should add `query parameter named search` for searching datatable to work.
 * @property `columns` pass column definition as describe in Tanstack Table `ColumDef` as an array
 * @property `url` URL must be defined in `project_source/endpoints/constanst.ts` as either string
 * or value from object
 * @property `params` filter params using filterbox which are appended to query parameters
 */
export default function DataTable({
  url,
  queryKey,
  columns,
  addNew = null,
  customSearch = false,
  params,
  tableName,
  actionTools = null,
  limit: upperLimit = 10,
  onRowClick,
}: IDataTable) {
  const [searchParams, setSearchParms] = useSearchParams({
    limit: "10",
    page: "0",
    searchQuery: "",
  });

  const page = parseInt(searchParams.get("page") ?? "0");
  const searchQuery = searchParams.get("search");
  const selectedItems = searchParams.get("selectedItems");
  const debouncesearch = useDebounce(searchQuery);
  const [limit, setLimit] = useState(upperLimit);
  const [data, setData] = useState([]);
  const [isOpenTableSetting, setIsOpenTableSettings] = useState(false);
  const { data: serverData, isLoading } = useGetHook({
    queryKey: [
      queryKey,
      `${queryKey}-${limit}-${page}-${JSON.stringify(
        params
      )}-${debouncesearch}`,
    ],
    url: url,
    params: {
      limit,
      offset: limit * (isNaN(page) ? 0 : page),
      search: debouncesearch,
      ...params,
    },
  });

  const table = useReactTable({
    data,
    columns,
    enableRowSelection: true,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  useEffect(() => {
    if (Array.isArray(serverData?.results)) {
      setData(serverData?.results ?? []);
    }
  }, [serverData]);

  useEffect(() => {
    table.setPageSize(limit);
  }, [limit, table]);

  return isLoading ? (
    <Template
      addNew={addNew}
      customSearch={!customSearch}
      noOfColumns={columns.length}
    />
  ) : (
    <>
      <div className={tw`w-full rounded-sm`}>
        <div className={tw` w-full flex gap-x-4 items-center`}>
          {!customSearch && (
            <div className={tw`py-2`}>
              <TableSearch addNew={addNew} />
            </div>
          )}
          <div className={tw`w-full flex justify-end gap-x-4 items-center `}>
            <div>{actionTools}</div>

            <div className={tw`mr-4`}>
              <TableSettingsLayout
                isOpenTableSetting={isOpenTableSetting}
                setIsOpenTableSettings={setIsOpenTableSettings}
                tableName={tableName}
                defaultColumns={columns}
                onSettingsLoaded={(settings) => {
                  if (settings?.page_limit != null) {
                    setLimit(settings.page_limit);
                    searchParams.set("limit", settings.page_limit.toString());
                    setSearchParms(searchParams);
                  }
                }}
              />
            </div>
          </div>
        </div>
        <div
          className={tw`bg-[#fefefe] rounded-md border border-[#dcdcdc]`}
          style={{ boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)" }}
        >
          <div className={tw`w-full overflow-auto z-0 rounded-t-md`}>
            <table className={tw`w-full`}>
              <TableHead table={table} />
              {isLoading ? (
                <TableBodyLoading noOfColumns={columns.length} />
              ) : !data?.length ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className={tw`text-[#949494] text-2xl leading-8 text-center py-10`}
                  >
                    No entries found.
                  </td>
                </tr>
              ) : (
                <TableBody table={table} onRowClick={onRowClick} />
              )}
            </table>
          </div>
          <TablePagination
            serverData={serverData}
            limit={limit}
            table={table}
            setLimit={setLimit}
          />
        </div>
      </div>
    </>
  );
}

